import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { FileWarning, Layers, Undo2, ChevronRight, History, CheckCircle2, AlertCircle } from "lucide-react";

const UNIDADES = [
  { slug: "petit", label: "Celebra Petit", color: "#16a34a" },
  { slug: "kermesita", label: "La Kermesita", color: "#db2777" },
  { slug: "bocatto", label: "Bocatto Catering", color: "#ea580c" },
];

const CONF_BADGE = {
  alto: "bg-emerald-100 text-emerald-800 border-emerald-200",
  medio: "bg-amber-100 text-amber-800 border-amber-200",
  bajo: "bg-stone-100 text-stone-600 border-stone-200",
};

export default function SinClasificar() {
  const [resumen, setResumen] = useState({ colecciones: [], total_huerfanos: 0 });
  const [colFiltro, setColFiltro] = useState(null);
  const [huerfanos, setHuerfanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seleccion, setSeleccion] = useState({}); // { "col|doc_id": "petit" }
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [motivo, setMotivo] = useState("");
  const [historial, setHistorial] = useState([]);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const cargarResumen = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/sin-clasificar/resumen`);
      setResumen(r.data || { colecciones: [], total_huerfanos: 0 });
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cargar resumen"); }
  }, []);

  const cargarHuerfanos = useCallback(async (col) => {
    setLoading(true);
    try {
      const q = col ? `?coleccion=${col}` : "";
      const r = await axios.get(`${API}/admin/sin-clasificar/listado${q}`);
      setHuerfanos(r.data?.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cargar huérfanos"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargarResumen(); cargarHuerfanos(null); }, [cargarResumen, cargarHuerfanos]);

  const cambiarColeccion = (col) => {
    setColFiltro(col);
    setSeleccion({});
    cargarHuerfanos(col);
  };

  const toggleSel = (h, unidad) => {
    const k = `${h.coleccion}|${h.doc_id}`;
    if (!unidad || unidad === "__none__") {
      const { [k]: _, ...rest } = seleccion;
      setSeleccion(rest);
    } else {
      setSeleccion({ ...seleccion, [k]: unidad });
    }
  };

  const seleccionarTodosSugeridos = () => {
    const nuevo = { ...seleccion };
    huerfanos.forEach((h) => {
      if (h.unidad_sugerida && (h.confianza === "alto" || h.confianza === "medio")) {
        nuevo[`${h.coleccion}|${h.doc_id}`] = h.unidad_sugerida;
      }
    });
    setSeleccion(nuevo);
  };

  const limpiarSeleccion = () => setSeleccion({});

  const abrirVistaPrevia = async () => {
    const items = Object.entries(seleccion).map(([k, unidad]) => {
      const [coleccion, doc_id] = k.split("|");
      return { coleccion, doc_id, unidad };
    });
    if (items.length === 0) { toast.error("Selecciona al menos un registro"); return; }
    try {
      const r = await axios.post(`${API}/admin/sin-clasificar/vista-previa`, { items });
      setPreviewData(r.data?.items || []);
      setPreviewOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const confirmarAsignacion = async () => {
    if (saving) return;
    setSaving(true);
    const items = Object.entries(seleccion).map(([k, unidad]) => {
      const [coleccion, doc_id] = k.split("|");
      return { coleccion, doc_id, unidad };
    });
    try {
      const r = await axios.post(`${API}/admin/sin-clasificar/asignar`, {
        items, motivo: motivo || "Asignación manual desde vista Sin Clasificar",
      });
      const nAp = r.data?.cantidad_aplicados || 0;
      const nEr = r.data?.cantidad_errores || 0;
      if (nAp > 0) {
        toast.success(`${nAp} registro${nAp !== 1 ? "s" : ""} asignado${nAp !== 1 ? "s" : ""} correctamente (audit ${r.data.audit_id})`);
      }
      if (nEr > 0) toast.warning(`${nEr} con errores`);
      setPreviewOpen(false);
      setSeleccion({});
      setMotivo("");
      cargarResumen();
      cargarHuerfanos(colFiltro);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al asignar"); }
    finally { setSaving(false); }
  };

  const cargarHistorial = async () => {
    try {
      const r = await axios.get(`${API}/admin/sin-clasificar/historial`);
      setHistorial(r.data?.entradas || []);
      setHistorialOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const revertir = async (audit_id) => {
    if (!window.confirm(`¿Revertir asignación ${audit_id}? Se restaurarán los valores anteriores.`)) return;
    try {
      const r = await axios.post(`${API}/admin/sin-clasificar/revertir/${audit_id}`);
      toast.success(`${r.data?.revertidos?.length || 0} registros revertidos`);
      cargarResumen();
      cargarHuerfanos(colFiltro);
      cargarHistorial();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al revertir"); }
  };

  return (
    <div className="space-y-4" data-testid="sin-clasificar-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
              <FileWarning className="w-6 h-6 text-amber-700" />
            </div>
            <div>
              <h1 className="font-display text-3xl text-stone-900">Sin Clasificar</h1>
              <p className="text-sm text-stone-500">
                <strong>¿Para qué sirve?</strong> Aquí aparecen eventos, cotizaciones y pagos que llegaron sin unidad de negocio asignada (ej. desde el cotizador público). Los clasificas manual o por lote para que aparezcan en la unidad correcta (Petit, Kermesita, Bocatto).
              </p>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={cargarHistorial} data-testid="btn-ver-historial">
          <History className="w-4 h-4 mr-2" /> Ver historial
        </Button>
      </div>

      {/* Alerta clave */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900 space-y-1">
            <p><strong>{resumen.total_huerfanos}</strong> registros sin clasificar en {resumen.colecciones?.filter(c => c.huerfanos > 0).length || 0} colecciones.</p>
            <p className="text-xs">Estos registros <strong>NO</strong> participan en descuentos de inventario, producción, costeo ni reservas hasta ser asignados. Ninguna asignación se ejecuta sin tu confirmación explícita.</p>
          </div>
        </CardContent>
      </Card>

      {/* Resumen por colección */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {(resumen.colecciones || []).map((c) => (
          <Card
            key={c.coleccion}
            className={`border cursor-pointer transition-all ${
              colFiltro === c.coleccion ? "border-amber-400 bg-amber-50/40" :
              c.huerfanos > 0 ? "border-stone-200 hover:border-amber-300" : "border-stone-100 opacity-60"
            }`}
            onClick={() => cambiarColeccion(c.coleccion === colFiltro ? null : c.coleccion)}
            data-testid={`resumen-col-${c.coleccion}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-stone-900">{c.coleccion}</p>
                  <p className="text-xs text-stone-500">
                    {c.clasificados}/{c.total} clasificados{c.usa_alias_legado ? " · alias legado ON" : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`font-display text-2xl ${c.huerfanos > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                    {c.huerfanos}
                  </p>
                  <p className="text-[10px] text-stone-500">huérfanos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Toolbar de lote */}
      <div className="sticky top-14 md:top-0 z-20 -mx-4 md:mx-0 px-4 md:px-0 py-2 bg-stone-50/95 backdrop-blur border-y border-stone-200">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-stone-700">
            <strong>{Object.keys(seleccion).length}</strong> seleccionado{Object.keys(seleccion).length !== 1 ? "s" : ""}
            {colFiltro && ` · filtro: ${colFiltro}`}
          </p>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={seleccionarTodosSugeridos} data-testid="btn-sel-sugeridos">
            Aceptar sugerencias (alto+medio)
          </Button>
          <Button size="sm" variant="outline" onClick={limpiarSeleccion} disabled={Object.keys(seleccion).length === 0}>
            Limpiar
          </Button>
          <Button size="sm" onClick={abrirVistaPrevia} disabled={Object.keys(seleccion).length === 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-vista-previa">
            <Layers className="w-4 h-4 mr-2" /> Vista previa ({Object.keys(seleccion).length})
          </Button>
        </div>
        {Object.keys(seleccion).length > 0 && (
          <p className="text-[11px] text-amber-800 mt-1.5 md:hidden">
            Nada se guarda hasta que hagas click en <strong>&quot;Vista previa&quot;</strong> arriba y luego <strong>&quot;Confirmar y asignar&quot;</strong>.
          </p>
        )}
      </div>

      {/* Tabla de huérfanos */}
      {loading ? (
        <div className="text-stone-500 text-sm p-4">Cargando huérfanos...</div>
      ) : huerfanos.length === 0 ? (
        <Card className="border-emerald-200">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
            <p className="text-sm text-stone-600">Sin huérfanos {colFiltro ? `en ${colFiltro}` : ""}.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto border border-stone-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-xs text-stone-500 text-left">
              <tr>
                <th className="p-2">Colección</th>
                <th className="p-2">Registro</th>
                <th className="p-2">Categoría</th>
                <th className="p-2">Fecha</th>
                <th className="p-2">Sugerencia</th>
                <th className="p-2">Confianza</th>
                <th className="p-2">Asignar a…</th>
              </tr>
            </thead>
            <tbody>
              {huerfanos.map((h) => {
                const k = `${h.coleccion}|${h.doc_id}`;
                const sel = seleccion[k];
                return (
                  <tr key={k} className={`border-t border-stone-100 ${sel ? "bg-amber-50/30" : ""}`} data-testid={`row-${h.coleccion}-${h.doc_id}`}>
                    <td className="p-2 text-xs text-stone-600">{h.coleccion}</td>
                    <td className="p-2">
                      <p className="font-medium text-stone-900 max-w-xs truncate">{h.nombre}</p>
                      <p className="text-[10px] text-stone-400">{h.doc_id}</p>
                    </td>
                    <td className="p-2 text-xs text-stone-600">{h.categoria || "—"}</td>
                    <td className="p-2 text-xs text-stone-500 whitespace-nowrap">{h.fecha ? String(h.fecha).slice(0, 10) : "—"}</td>
                    <td className="p-2 text-xs">
                      {h.unidad_sugerida ? (
                        <div>
                          <p className="font-medium capitalize">{h.unidad_sugerida}</p>
                          <p className="text-[10px] text-stone-500 max-w-xs">{h.motivo}</p>
                        </div>
                      ) : <span className="text-stone-400">manual</span>}
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] border ${CONF_BADGE[h.confianza] || CONF_BADGE.bajo}`}>
                        {h.confianza}
                      </span>
                    </td>
                    <td className="p-2">
                      <Select value={sel || "__none__"} onValueChange={(v) => toggleSel(h, v)}>
                        <SelectTrigger className="w-40 h-8" data-testid={`sel-${h.coleccion}-${h.doc_id}`}>
                          <SelectValue placeholder="Sin seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sin seleccionar —</SelectItem>
                          {UNIDADES.map((u) => <SelectItem key={u.slug} value={u.slug}>{u.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog Vista previa */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="dialog-preview">
          <DialogHeader>
            <DialogTitle>Vista previa de asignación</DialogTitle>
            <DialogDescription className="text-xs">
              Revisa exactamente qué campos se van a actualizar antes de confirmar. Nada se ejecuta hasta que hagas click en &ldquo;Confirmar y asignar&rdquo;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Motivo / nota (opcional)</Label>
              <Textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: limpieza histórica autorizada por..." />
            </div>
            <div className="border border-stone-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-xs text-stone-500 text-left">
                  <tr>
                    <th className="p-2">Colección</th>
                    <th className="p-2">Registro</th>
                    <th className="p-2">Actual</th>
                    <th className="p-2">Nueva</th>
                    <th className="p-2">Campos</th>
                    <th className="p-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((p, i) => (
                    <tr key={i} className="border-t border-stone-100">
                      <td className="p-2 text-xs">{p.coleccion}</td>
                      <td className="p-2 max-w-xs truncate">{p.nombre}</td>
                      <td className="p-2 text-xs text-stone-500">
                        {p.unidad_actual ? JSON.stringify(p.unidad_actual) : "—"}
                      </td>
                      <td className="p-2 text-xs">
                        <span className="font-medium capitalize">{p.unidad_nueva}</span>
                      </td>
                      <td className="p-2 text-[10px] text-stone-500">{(p.campos_a_actualizar || []).join(", ")}</td>
                      <td className="p-2">
                        {p.status === "ok" ? (
                          <span className="text-emerald-700 text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> OK</span>
                        ) : (
                          <span className="text-red-700 text-xs">{p.motivo || "Error"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-stone-500 p-3 bg-stone-50 rounded-lg">
              <strong>Registro de auditoría:</strong> se guardará quién autorizó, cuándo, valores anteriores por cada registro y las reglas aplicadas. La asignación es reversible desde el historial.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Cancelar</Button>
            <Button
              onClick={confirmarAsignacion}
              disabled={saving || previewData.filter(p => p.status === "ok").length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="btn-confirmar-asignacion"
            >
              {saving ? "Asignando..." : `Confirmar y asignar (${previewData.filter(p => p.status === "ok").length})`}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Historial */}
      <Dialog open={historialOpen} onOpenChange={setHistorialOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historial de asignaciones</DialogTitle>
            <DialogDescription className="text-xs">
              Cada asignación es reversible desde aquí. La reversión restaura los valores anteriores exactos.
            </DialogDescription>
          </DialogHeader>
          {historial.length === 0 ? (
            <p className="text-stone-500 text-sm py-4">Sin asignaciones registradas aún.</p>
          ) : (
            <div className="space-y-2">
              {historial.map((h) => (
                <Card key={h.audit_id} className={h.revertido ? "border-stone-200 opacity-60" : "border-stone-200"}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="min-w-0">
                        <p className="font-mono text-xs text-stone-500">{h.audit_id}</p>
                        <p className="text-sm">
                          <strong>{h.cantidad_items}</strong> registro{h.cantidad_items !== 1 ? "s" : ""} · por {h.usuario_email || h.usuario_id}
                        </p>
                        <p className="text-xs text-stone-500">{h.timestamp?.slice(0, 19).replace("T", " ")}</p>
                        {h.motivo && <p className="text-xs text-stone-600 mt-1 italic">&ldquo;{h.motivo}&rdquo;</p>}
                        {h.revertido && <span className="text-xs text-red-700">✗ Revertida</span>}
                      </div>
                      {!h.revertido && (
                        <Button size="sm" variant="outline" onClick={() => revertir(h.audit_id)}>
                          <Undo2 className="w-3 h-3 mr-1" /> Revertir
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
